/**
 * Typed HTTP client for the Care and Clean API.
 * - Cookie-based session (credentials: "include"); no tokens in storage.
 * - Sends the CSRF header the API requires on state-changing requests.
 * - Normalizes the API error envelope into ApiError.
 */

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "/api/v1";

export type ApiErrorDetail = { path?: string; message: string };

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly requestId?: string;

  constructor(status: number, code: string, message: string, details?: unknown, requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  /** Field-level validation messages keyed by field path (without the "body." prefix). */
  get fieldErrors(): Record<string, string> {
    if (!Array.isArray(this.details)) return {};
    const out: Record<string, string> = {};
    for (const detail of this.details as ApiErrorDetail[]) {
      if (detail.path) out[detail.path.replace(/^(body|query|params)\./, "")] ??= detail.message;
    }
    return out;
  }
}

export const SESSION_EXPIRED_EVENT = "cc:session-expired";

export type Paginated<T> = {
  items: T[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
};

type QueryValue = string | number | boolean | null | undefined | string[];
export type QueryParams = Record<string, QueryValue>;

export function toSearchParams(params?: QueryParams) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      if (value.length) search.set(key, value.join(","));
    } else {
      search.set(key, String(value));
    }
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

type RequestOptions = { method?: string; body?: unknown; params?: QueryParams; signal?: AbortSignal; rawBody?: Blob };

async function rawRequest(path: string, options: RequestOptions = {}) {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };
  if (method !== "GET") headers["X-CSRF-Protection"] = "1";
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (options.rawBody) headers["Content-Type"] = options.rawBody.type || "application/octet-stream";

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}${toSearchParams(options.params)}`, {
      method,
      headers,
      credentials: "include",
      body: options.rawBody ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
      signal: options.signal,
    });
  } catch (error) {
    if ((error as Error).name === "AbortError") throw error;
    throw new ApiError(0, "NETWORK_ERROR", "Could not reach the server. Check your connection and try again.");
  }

  if (!response.ok) {
    let payload: { error?: { code?: string; message?: string; details?: unknown }; requestId?: string } = {};
    try {
      payload = await response.json();
    } catch {
      // Non-JSON error (e.g. proxy failure).
    }
    const error = new ApiError(
      response.status,
      payload.error?.code ?? "HTTP_ERROR",
      payload.error?.message ?? `Request failed (${response.status}).`,
      payload.error?.details,
      payload.requestId,
    );
    // Business sessions only; the platform admin dashboard handles its own sign-in.
    if (response.status === 401 && path !== "/auth/login" && path !== "/auth/me" && !path.startsWith("/platform/")) {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
    throw error;
  }
  return response;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<{ data: T; meta?: Record<string, unknown> }> {
  const response = await rawRequest(path, options);
  const payload = (await response.json()) as { data: T; meta?: Record<string, unknown> };
  return payload;
}

export const api = {
  get: async <T>(path: string, params?: QueryParams, signal?: AbortSignal) => (await request<T>(path, { params, signal })).data,
  getPage: async <T>(path: string, params?: QueryParams, signal?: AbortSignal): Promise<Paginated<T>> => {
    const payload = await request<T[]>(path, { params, signal });
    return { items: payload.data, meta: payload.meta as Paginated<T>["meta"] };
  },
  getWithMeta: <T>(path: string, params?: QueryParams) => request<T>(path, { params }),
  post: async <T>(path: string, body?: unknown) => (await request<T>(path, { method: "POST", body: body ?? {} })).data,
  postWithMeta: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body: body ?? {} }),
  patch: async <T>(path: string, body: unknown) => (await request<T>(path, { method: "PATCH", body })).data,
  delete: async <T>(path: string, body?: unknown) => (await request<T>(path, { method: "DELETE", body })).data,
  /** Sends a file as the raw request body (e.g. an image upload). */
  upload: async <T>(path: string, file: Blob, method = "PUT") => (await request<T>(path, { method, rawBody: file })).data,

  /** Downloads an export (CSV/XLSX) using the session cookie. */
  download: async (path: string, params?: QueryParams) => {
    const response = await rawRequest(path, { params });
    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? "export";
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

export function errorMessage(error: unknown, fallback = "Something went wrong. Please try again.") {
  if (error instanceof ApiError) return error.message;
  return fallback;
}
